"use client";

import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/button";
import { LocaleSwitcher } from "@/features/locale-switcher";
import { useTranslations } from "next-intl";
import { MoIco32TopMenu } from "@/shared/assets/icons/mo_ico_32_top_menu";
import { useBreakpoint } from "@/shared/hooks/use-breakpoint";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/shared/ui/sheet";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/shared/ui/collapsible";
import { Ico20ArrowRight } from "@/shared/assets/icons/ico_20_arrow_right";
import { useAuth } from "@/shared/hooks/use-auth";

const MENU_ITEMS = [
  {
    label: "gnb_menu_1",
    href: "/plan",
  },
  {
    label: "gnb_menu_2",
    href: "/download",
  },
  {
    label: "gnb_menu_3",
    href: "/solution",
  },
];

const SUPPORT_MENU = [
  { label: "gnb_menu_4_1", href: "/faq" },
  { label: "gnb_menu_4_2", href: "/notice" },
  { label: "gnb_menu_4_3", href: "/inquiry" },
];

const MO_MY_PAGE_MENU = [
  { label: "f_my_pro_tab_1", href: "/mypage" },
  { label: "f_my_pro_tab_2", href: "/mypage/plan" },
  { label: "f_my_pro_tab_3", href: "/mypage/billing" },
  { label: "f_my_pro_tab_4", href: "/mypage/payment" },
  { label: "f_my_pro_tab_5", href: "/mypage/inquiry" },
];

export function HeaderClient({ isLoggedIn }: { isLoggedIn: boolean }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const pathname = usePathname();
  const t = useTranslations();
  const { isPc } = useBreakpoint();
  const isMainPage = pathname === "/";
  const isTransparentTheme = isMainPage && !isScrolled;
  const { isLoggedIn: clientIsLoggedIn } = useAuth(isLoggedIn);

  // 이미지와 버튼 variant용 (JS로만 처리 가능한 것들)
  // PC가 아니면 hover 상태를 무시
  const shouldShowWhiteTheme = isTransparentTheme && (!isPc || !isHovered);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsScrolled(currentScrollY > 50);

      // 모바일/태블릿에서만 헤더 숨김/보임 처리
      if (!isPc) {
        if (currentScrollY > lastScrollY && currentScrollY > 100) {
          // 아래로 스크롤 - 헤더 숨김
          setIsHeaderVisible(false);
        } else if (currentScrollY < lastScrollY) {
          // 위로 스크롤 - 헤더 보임
          setIsHeaderVisible(true);
        }
        setLastScrollY(currentScrollY);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isPc, lastScrollY]);

  return (
    <header
      className={cn(
        "group pc:h-[94px] pc:px-[120px] tb:h-20 tb:px-10 fixed top-0 right-0 left-0 z-50 flex h-12 items-center justify-between border-b px-4 transition-all duration-300",
        isMainPage ? "opacity-0" : "opacity-100",
        isTransparentTheme
          ? "border-b-transparent bg-transparent hover:border-b-gray-100 hover:bg-white"
          : "border-b-gray-100 bg-white",
        !isPc && !isHeaderVisible && "-translate-y-full transform",
      )}
      onMouseEnter={() => isPc && setIsHovered(true)}
      onMouseLeave={() => isPc && setIsHovered(false)}
    >
      <Link href="/" data-header-logo>
        <img
          src={shouldShowWhiteTheme ? "/images/home/logo.webp" : "/images/home/logo_gray.webp"}
          alt="logo"
          className="pc:w-[174px] pc:h-[38px] tb:w-[137px] tb:h-[30px] h-[24px] w-[110px]"
        />
      </Link>

      <nav
        className={cn(
          "body-md-regular pc:flex hidden h-full items-center gap-9",
          isTransparentTheme ? "text-white group-hover:text-gray-900" : "text-gray-900",
        )}
      >
        {MENU_ITEMS.map((item) => (
          <Link
            key={item.label}
            href={item.href}
            className="hover:!text-primary-500 hover:!border-primary-500 flex h-full items-center px-6 transition-colors duration-300 hover:!mb-[-2px] hover:!border-b-2"
          >
            {t(item.label)}
          </Link>
        ))}

        {/* Support 메뉴 - hover 시 서브메뉴 표시 */}
        <div className="group/support relative flex h-full items-center">
          <div className="group-hover/support:!text-primary-500 group-hover/support:!border-primary-500 hover:!text-primary-500 hover:!border-primary-500 flex h-full cursor-pointer items-center px-6 transition-colors duration-300 group-hover/support:!mb-[-2px] group-hover/support:!border-b-2 hover:!mb-[-2px] hover:!border-b-2">
            {t("gnb_menu_4")}
          </div>

          {/* 서브메뉴 배경 - 헤더처럼 가로로 길게 */}
          <div className="fixed top-[94px] right-0 left-0 z-40 hidden h-[62px] border-b border-gray-100 bg-white shadow-sm group-hover/support:block"></div>

          {/* 서브메뉴 실제 메뉴들 - Support 바로 아래 */}
          <div className="absolute top-full left-[-97px] z-50 hidden h-[62px] items-center gap-6 whitespace-nowrap group-hover/support:flex">
            {SUPPORT_MENU.map((item) => (
              <Link
                key={item.label}
                href={item.href}
                className="body-md-regular flex h-full items-center px-6 py-[14px] break-keep text-gray-400 transition-colors hover:text-gray-900"
              >
                {t(item.label)}
              </Link>
            ))}
          </div>
        </div>
      </nav>

      <nav className="pc:flex hidden gap-2">
        {clientIsLoggedIn ? (
          <Link href="/mypage" prefetch={false}>
            <Button variant={shouldShowWhiteTheme ? "tertiary" : "secondary"} size="md">
              {"My Page"}
            </Button>
          </Link>
        ) : (
          <Link href="/login">
            <Button variant={shouldShowWhiteTheme ? "tertiary" : "secondary"} size="md">
              {t("gnb_menu_5")}
            </Button>
          </Link>
        )}
        <LocaleSwitcher isMainPage={isMainPage} isScrolled={isScrolled} setIsHovered={setIsHovered} />
      </nav>

      <Sheet>
        <SheetTrigger asChild>
          <button className={cn("pc:hidden", isTransparentTheme ? "text-white" : "text-gray-900")}>
            <MoIco32TopMenu className="tb:size-11" />
          </button>
        </SheetTrigger>
        <SheetContent className="tb:overflow-y-auto tb:scrollbar-hide w-full gap-0 border-none bg-white sm:max-w-full">
          <SheetHeader className="tb:h-20 tb:px-10 tb:px-10 tb:py-[18px] flex h-12 items-end justify-center px-4 py-[7px]">
            <SheetTitle className="sr-only">Sidebar</SheetTitle>
            <LocaleSwitcher isMainPage={isMainPage} isScrolled={isScrolled} setIsHovered={setIsHovered} />
          </SheetHeader>
          <div className="bg-secondary-100 tb:pt-10 flex h-full flex-col gap-0 pt-5">
            {MENU_ITEMS.filter((item) => item.href !== "/download").map((item) => (
              <SheetClose key={item.label} asChild>
                <Link
                  href={item.href}
                  className="body-xl-bold tb:px-10 tb:py-5 flex items-center px-5 py-3 break-keep text-gray-900"
                >
                  {t(item.label)}
                </Link>
              </SheetClose>
            ))}
            <Collapsible>
              <CollapsibleTrigger className="group body-xl-bold data-[state=open]:text-primary-500 tb:px-10 tb:py-5 flex w-full items-center justify-between px-5 py-3 break-keep text-gray-900">
                {t("gnb_menu_4")}
                <Ico20ArrowRight className="tb:size-6 rotate-90 transition-transform duration-300 group-data-[state=open]:rotate-[270deg]" />
              </CollapsibleTrigger>

              <CollapsibleContent className="animate-in fade-in-0 slide-in-from-bottom-1 tb:gap-8 tb:p-[50px] flex flex-col gap-6 bg-gray-50 p-7.5">
                {SUPPORT_MENU.map((item) => (
                  <SheetClose key={item.label} asChild>
                    <Link href={item.href} className="body-lg-medium flex items-center break-keep text-gray-700">
                      {t(item.label)}
                    </Link>
                  </SheetClose>
                ))}
              </CollapsibleContent>
            </Collapsible>
            {clientIsLoggedIn ? (
              <Collapsible>
                <CollapsibleTrigger className="group body-xl-bold data-[state=open]:text-primary-500 tb:px-10 tb:py-5 flex w-full items-center justify-between px-5 py-3 break-keep text-gray-900">
                  {"My Page"}
                  <Ico20ArrowRight className="tb:size-6 rotate-90 transition-transform duration-300 group-data-[state=open]:rotate-[270deg]" />
                </CollapsibleTrigger>

                <CollapsibleContent className="animate-in fade-in-0 slide-in-from-bottom-1 tb:gap-8 tb:p-[50px] flex flex-col gap-6 bg-gray-50 p-7.5">
                  {MO_MY_PAGE_MENU.map((item) => (
                    <SheetClose key={item.label} asChild>
                      <Link
                        href={item.href}
                        className="body-lg-medium flex items-center break-keep text-gray-700"
                        prefetch={false}
                      >
                        {t(item.label)}
                      </Link>
                    </SheetClose>
                  ))}
                </CollapsibleContent>
              </Collapsible>
            ) : (
              <SheetClose asChild>
                <Link
                  href="/login"
                  className="body-xl-bold tb:px-10 flex items-center px-5 py-3 break-keep text-gray-900"
                >
                  {t("gnb_menu_5")}
                </Link>
              </SheetClose>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
}
